import { useState } from "react";
import { Autocomplete, Stack, TextField, useTheme } from "@mui/material";
import { FoodsIcon, FoodsTags, FoodsTooltip, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { AuditLogFacets } from "src/api/client-api/auditLog/auditLogSchemas";
import { splitAuditLogQueryToken, type AuditLogQueryField } from "./auditLogQuery";
import { suggestAuditLogQueryTokens, type AuditLogSuggestion } from "./auditLogQuerySuggestions";

type AuditLogQueryInputProps = {
    tokens: string[];
    onTokensChange: (tokens: string[]) => void;
    facets: AuditLogFacets | undefined;
};

export const AuditLogQueryInput = (props: AuditLogQueryInputProps) => {
    const { tokens, onTokensChange, facets } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const [fragment, setFragment] = useState("");

    const suggestions = suggestAuditLogQueryTokens(fragment, facets);

    const fieldHints: Record<AuditLogQueryField, string> = {
        action: t("settings.audit_log.query.hint.action", "Creation, update or deletion"),
        actor_type: t("settings.audit_log.query.hint.actor_type", "Human account or service account"),
        actor_id: t("settings.audit_log.query.hint.actor_id", "One specific actor"),
        entity_type: t("settings.audit_log.query.hint.entity_type", "Nature of the modified object"),
        entity_id: t("settings.audit_log.query.hint.entity_id", "One specific object, by identifier"),
        domain: t("settings.audit_log.query.hint.domain", "Broad functional domain"),
        scope: t("settings.audit_log.query.hint.scope", "Organization or platform"),
        scope_id: t("settings.audit_log.query.hint.scope_id", "One specific organization"),
    };

    const removeToken = (index: number) => onTokensChange(tokens.filter((_token, position) => position !== index));

    return (
        <Autocomplete<AuditLogSuggestion, true, false, true>
            multiple
            freeSolo
            options={suggestions}
            filterOptions={(options) => options}
            value={tokens as unknown as AuditLogSuggestion[]}
            inputValue={fragment}
            onInputChange={(_event, next, reason) => {
                if (reason !== "reset") {
                    setFragment(next);
                }
            }}
            onChange={(_event, next) => {
                const last = next.at(-1);

                if (typeof last === "object" && last !== null && last.value === null) {
                    setFragment(last.token);
                    return;
                }

                setFragment("");
                onTokensChange(next.map((item) => (typeof item === "string" ? item : item.token)));
            }}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.token)}
            renderTags={(value) =>
                value.map((item, index) => {
                    const token = typeof item === "string" ? item : item.token;
                    const { field, value: tokenValue, isDropped } = splitAuditLogQueryToken(token);

                    return (
                        <FoodsTooltip
                            key={`${index}-${token}`}
                            title={
                                isDropped ?
                                    t(
                                        "settings.audit_log.query.remove_excluded_token",
                                        "This value is excluded — click to bring it back",
                                    )
                                :   t("settings.audit_log.query.remove_token", "Remove this filter")
                            }
                        >
                            <FoodsTags
                                variant={isDropped ? "tags-grey" : "tags-blue"}
                                size="display"
                                startIcon={
                                    isDropped ? "minus"
                                    : field === null ?
                                        "search"
                                    :   undefined
                                }
                                label={field === null ? tokenValue : `${field} :`}
                                detail={field === null ? undefined : tokenValue}
                                endIcon="close"
                                onClick={() => removeToken(index)}
                                sx={{
                                    marginRight: theme.spacing(0.5),
                                    "& .MuiButton-startIcon > *, & .MuiButton-endIcon > *": {
                                        display: "flex",
                                        alignItems: "center",
                                        lineHeight: 1,
                                    },
                                }}
                            />
                        </FoodsTooltip>
                    );
                })
            }
            renderOption={(optionProps, option) => {
                const { key, ...rest } = optionProps;
                const hint = option.value === null && option.field !== null ? fieldHints[option.field] : option.hint;

                return (
                    <li key={key} {...rest}>
                        <Stack direction="row" justifyContent="space-between" gap={theme.spacing(2)} width="100%">
                            <Typography variant="small-medium">{option.token}</Typography>
                            {hint !== null && hint !== undefined && (
                                <Typography variant="small-regular" color="text.secondary">
                                    {hint}
                                </Typography>
                            )}
                        </Stack>
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size="small"
                    placeholder={
                        tokens.length === 0 ?
                            t("settings.audit_log.query.placeholder", "action:delete actor_type:service marie")
                        :   ""
                    }
                    inputProps={{
                        ...params.inputProps,
                        "aria-label": t("settings.audit_log.query.aria_label", "Search audit log"),
                    }}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                                    {params.InputProps.endAdornment}
                                    <FoodsIcon icon="search" size={2} />
                                </Stack>
                            ),
                        },
                    }}
                />
            )}
        />
    );
};
